import { format } from "date-fns";
import { StatusTagType } from "@components/StatusTagGeneric/StatusTagGeneric";
import {
  financialStatusAcronym,
  financialStatusDescriptionPTBR,
  freighTypesEnum,
} from "../models/ENUM_MY_ORDERS";
import { PixApiResponse } from "../models/IPixPayment";
import { PixPayload } from "../models/IPixPayment";

export const getFinancialStatusPTBR = (status: string) => {
  switch (status) {
    case financialStatusAcronym.BL_BLOQUEADO: {
      return financialStatusDescriptionPTBR.BLOQUEADO;
    }
    case financialStatusAcronym.LIB_LIBERADO: {
      return financialStatusDescriptionPTBR.LIBERADO;
    }
    case financialStatusAcronym.FT_FATURADO: {
      return financialStatusDescriptionPTBR.FATURADO;
    }
    case financialStatusAcronym.DEV_DEVOLUCAO: {
      return financialStatusDescriptionPTBR.DEVOLUCAO;
    }
    default: {
      return "Indefinido";
    }
  }
};

export const getFinancialStatus = (statusFinancial: string) => {
  switch (statusFinancial) {
    case financialStatusAcronym.BL_BLOQUEADO: {
      return StatusTagType.ERROR;
    }
    case financialStatusAcronym.LIB_LIBERADO: {
      return StatusTagType.WARNING;
    }
    case financialStatusAcronym.FT_FATURADO: {
      return StatusTagType.SUCCESS;
    }
    case financialStatusAcronym.DEV_DEVOLUCAO: {
      return StatusTagType.NONE;
    }
    default: {
      return StatusTagType.NONE;
    }
  }
};

export const isBlockCalendarDelivery = (
  freightType: string,
  orderStatus?: string
): boolean => {
  if (orderStatus === "LIB") {
    return false;
  }

  // Para ordens bloqueadas, permita a alteração a qualquer momento.
  if (orderStatus === "BL") {
    return false;
  }

  if (orderStatus === "FT") {
    return true;
  }

  const currentHour = new Date().getHours();
  const isCIFBlocked = freightType === freighTypesEnum.CIF && currentHour > 13;
  const isFOBBlocked = freightType === freighTypesEnum.FOB && currentHour > 14;

  return isCIFBlocked || isFOBBlocked;
};

export const getStatusColorStatusFinancial = (status: string): string => {
  switch (status) {
    case "BL":
      return "#D91F05";
    case "LIB":
      return "#FFB800";
    case "FT":
      return "#0BB873";
    case "carregamento":
    case "aguardando carregamento":
    case "carregamento finalizado":
    case "carregamento cancelado":
    case "DEV":
      return "#3E3D3D";
    default:
      return "#3E3D3D";
  }
};

/**
 * Retorna o texto de exibição para o criador do pedido.
 * Prioriza nome > email > fallback "-".
 */
export function getCreatorDisplayName(
  creatorName?: string | null,
  creatorEmail?: string | null
): string {
  return creatorName || creatorEmail || "-";
}

/**
 * Mapeia o statusAtem (BL, LIB, FT, DEV) para o formato esperado pelo modal de pagamento Pix.
 */
export function mapOrderStatus(
  statusAtem?: string
): "BLOQUEADO" | "LIBERADO" | "FATURADO" {
  switch (statusAtem) {
    case "FT":
      return "FATURADO";
    case "LIB":
    case "D":
    case "A":
      return "LIBERADO";
    default:
      return "BLOQUEADO";
  }
}

/**
 * Formata a data de entrega para exibição, tratando a data "vazia" ("0000-00-00")
 * retornada pela API sem quebrar em runtime quando o valor não é uma instância de Date válida.
 */
export function formatDeliveryDate(date: Date | string): string {
  if (String(date) === "0000-00-00") return "";
  return format(new Date(date), "dd/MM/yyyy");
}

export function mapPixApiToPayload(data: PixApiResponse[]): PixPayload[] {
  return data.map((item) => ({
    id: item.UUID,
    copiaECola: item.qrcode,
    status: item.isPaid ? "pago" : "pendente",
    valor: Number(item.netValue),
    uuid: item.UUID,
    main: item.main,
    transactionId: item.transactionId ?? undefined,
    receiverName: item.receiverName ?? undefined,
    bankName: item.bankName ?? undefined,
    issueDate: item.issueDate ? formatPixDate(item.issueDate) : undefined,
    paymentStatus: item.paymentStatus ?? undefined,
    paymentMethod: item.paymentMethod ?? undefined,
  }));
}

function formatPixDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    const parts = new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Manaus",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? "";

    return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get(
      "minute"
    )}`;
  } catch {
    return isoDate;
  }
}
